import { GUARD_STRUCTURE, MethodConstantKey } from './cohort-guard-defaults';

export interface GuardHelp {
  what: string;
  why: string;
  how: string;
  when: string;
}

export const GUARD_HELP_TEXT: Record<string, GuardHelp> = {
  channel: {
    what: 'The sales channel (whatever the platform) and the cohort model (subscription or one-off purchase cohorts) whose settings you are looking at.',
    why: 'Each channel and cohort model has its own customers and buying habits, so each keeps its own settings and its own defaults.',
    how: 'Every setting on this screen is stored separately for each channel and cohort model and worked out from that model\'s own history.',
    when: 'Switch here to review or change another channel or cohort model. A change here never spills over to the others.'
  },
  basic: {
    what: 'The three decisions people make. Every value carries a label: System default or Set by user.',
    why: 'Keeping the everyday choices together means a scenario can be run without touching the rules underneath.',
    how: 'A blank box uses the system value, shown in light grey, and updates by itself whenever the data refreshes. Typing a value, even the same number, makes it Set by user and keeps it fixed until you clear the box.',
    when: 'When you know something the data does not, such as the month the business changed, or when you want a deliberate bull or bear scenario.'
  },
  legacyCutoff: {
    what: 'The month that splits early cohorts from the rest. Cohorts that started before it become one bundle; cohorts from this month on are forecast one by one.',
    why: 'Early history is often unreliable: a test launch, a platform change or patchy data. Learning from it can push forecasts far above anything customers really do.',
    how: 'System default: the start of the first run of {steadyRun} months in a row where every cohort is big enough to learn from. The bundle still counts in your totals and is forecast as a whole.',
    when: 'When you know the month the business changed or the data became reliable. Moving it later is safer from odd data, but leaves less history for older ages.'
  },
  trendLimits: {
    what: 'The largest rise or fall in repeat rate the forecast may carry forward in a year, set separately for each age group.',
    why: 'Retention trends are real but noisy. Without a limit, one good year can be projected as ever improving retention.',
    how: 'System default: each age group\'s typical year on year move across the complete years of history, so one exceptional year cannot set it. With under 2 complete years, each age group uses the size of the trend measured over the last {trendWindow} months, and with too little history to measure it, the trend is held at no change.',
    when: 'Widen it for a bull case and narrow it for a cautious case. Change a single age group when you know why that stage is changing, for example a new welcome offer lifting the first repeat order.'
  },
  trendWindow: {
    what: 'How many months of cohorts are compared to measure the trend: the latest ones against the same number just before them, separately for each age group.',
    why: 'Whole years on each side cancel seasonal swings, so a busy season is never mistaken for a trend. A longer window is steadier but needs more history.',
    how: 'System default {trendWindow} months. Choosing 24 months is saved for this channel and cohort model only, and the trend pill shows the change over the chosen window. With too little history to compare, the trend is held at no change.',
    when: 'Use 24 months only with at least 4 years of cohorts; with less, the trend measures nothing.'
  },
  trendApplications: {
    what: 'How many years the measured trend is applied for.',
    why: 'One year of evidence supports one year of projection. Applying the trend every year compounds it, which is how forecasts drift far from reality.',
    how: 'System default {trendApplications}: the change is applied once, then held flat for the rest of the forecast.',
    when: 'Raise it to 2 only for a knowing bull or bear case, and say so when the forecast is shared.'
  },
  advanced: {
    what: 'The rules that keep forecasts grounded: which history the forecast learns from, where older cohorts settle, and the safety nets.',
    why: 'These rules stop odd data and runaway trends reaching the forecast. Two are worth a judgement call; the rest look after themselves.',
    how: 'Every value is worked out from this channel and cohort model\'s own history and shown with its reason. Each section shows how many cohorts or cells it affected, counted from the calculation behind each cell.',
    when: 'Open them when you are investigating a forecast or a red cell. Only the boxes can be changed; everything else is read only.'
  },
  legacy: {
    what: 'Groups the early cohorts into one block that is forecast as a whole.',
    why: 'It keeps unreliable early history out of the learning while still counting every order it brings in.',
    how: 'Each future month of the bundle starts from the same month last year and is reduced by the share kept each month.',
    when: 'To bundle nothing, set Bundle cohorts that started before to your first cohort month.'
  },
  legacyRetention: {
    what: 'The share of last month\'s bundle orders that is kept this month.',
    why: 'Old customers drift away slowly. The forecast should shrink the bundle at the pace the data shows.',
    how: 'Measured from the bundle\'s last 12 months against the 12 before. With less history, it uses how mature cohorts fade.',
    when: 'Only if you know old customers are leaving faster than before, for example because an old product is being discontinued.'
  },
  legacyCeiling: {
    what: 'The highest share of bundle orders that can be kept each month.',
    why: 'A closed group of customers cannot grow, so the bundle should never be forecast to increase.',
    how: 'Fixed at 100%.',
    when: 'Never.'
  },
  credible: {
    what: 'Decides which cohorts are big enough to shape other cohorts\' forecasts.',
    why: 'A very small cohort says very little: one keen customer can make its repeat rate look enormous.',
    how: 'Cohorts below the minimum still get a forecast of their own, built from the bigger cohorts, and their orders still count in your totals. They never teach the engine.',
    when: 'Rarely. Raise the minimum if an odd forecast traces back to a small cohort.'
  },
  minSize: {
    what: 'The first month orders a cohort needs before the engine learns from it.',
    why: 'Each average needs enough real repeat orders behind it to be trusted.',
    how: '{evidenceTarget} expected repeat orders ÷ (typical repeat rate × cohorts averaged), rounded up. One-off purchase models repeat less often, so they need bigger cohorts.',
    when: 'Raise it if an odd forecast traces back to a small cohort. Lower it only if too few cohorts are left to learn from.'
  },
  k: {
    what: 'How many recent cohorts are averaged for each age.',
    why: 'Averaging smooths out a single unusually good or bad month.',
    how: 'It is the cohorts setting of the forecast method, {k} by default, and is also the minimum number of cohorts the trend and the long-term fade need.',
    when: 'Change it in the forecast method, so it is only ever set in one place.'
  },
  tail: {
    what: 'How cohorts carry on past the ages that enough well-sized cohorts have reached.',
    why: 'Real cohorts keep fading slowly. Holding them at one flat rate overstates older cohorts, and jumping to an average ignores where each cohort really is.',
    how: 'Each cohort continues from its own latest rate, actual or forecast, and keeps the fade for its age each month. That fade comes from the cohorts that reached the age, blended with the overall monthly fade when fewer than {k} have. With Monthly seasonality on, the month\'s seasonal factor is applied too. The fade never goes above 100%.',
    when: 'Nothing to change. A high count means this channel has little long-term history yet.'
  },
  tailFade: {
    what: 'The share of a cohort\'s orders kept from one month to the next, once it is past the well-evidenced ages.',
    why: 'Measured from your own mature cohorts, so it follows how your customers really fade.',
    how: 'The size-weighted average month on month change over the {tailWindow} most recent well-evidenced ages from age {tailStartAge} up. With fewer than {tailWindow} such ages, the bundle\'s share of orders kept each month, or the ages there are when there is no bundle. Without either, it needs a decision.',
    when: 'Read only. If it needs a decision, set Share of bundle orders kept each month in Bundle early cohorts.'
  },
  tailFlat: {
    what: 'One steady repeat rate for ages older than any well-sized cohort has reached yet.',
    why: 'There is no real evidence for those ages yet, and mature customers tend to settle at a steady rate.',
    how: 'The average repeat rate across the well-evidenced ages from age {tailStartAge} up.',
    when: 'Saved with this published version. Newer versions use the Long-term fade.'
  },
  tailRate: {
    what: 'The repeat rate every cohort settles at once it is older than the ages with enough evidence.',
    why: 'Without real evidence for those ages, a steady rate from mature customers is the most honest assumption.',
    how: 'The average repeat rate across the {tailWindow} most recent well-evidenced ages from age {tailStartAge} up.',
    when: 'Read only. It follows your data.'
  },
  tailWindow: {
    what: 'How many mature ages are used to measure the monthly fade.',
    why: '{tailWindow} ages cover every calendar month once, so seasonality evens out.',
    how: 'The {tailWindow} most recent well-evidenced ages from age {tailStartAge} up.',
    when: 'Read only.'
  },
  season: {
    what: 'How much busier or quieter each calendar month is for repeat orders than an average month.',
    why: 'Repeat orders rise and fall with the calendar. A factor per month lets the forecast follow that pattern instead of flattening it.',
    how: 'For each calendar month, the size-weighted average of each well-sized cohort\'s actual rate divided by the average curve at the same age, scaled so the 12 factors average 1. A month with fewer than {k} cohorts uses 1. With under 12 months of actuals every factor is 1.',
    when: 'Read only. Turn on Monthly seasonality in the forecast method to use it. Continue own curve uses this month\'s factor divided by last month\'s, so it never compounds.'
  },
  cap: {
    what: 'A ceiling no forecast month can go above, set separately for each age group.',
    why: 'It is a safety net that catches anything the other rules miss. One ceiling for every age would be far too loose for older cohorts, whose real rates are much lower.',
    how: 'For each age group: the highest real repeat rate any well-sized cohort has reached in that group, multiplied by that group\'s largest rise allowed.',
    when: 'Nothing to change. If cells are capped, something upstream needs investigating.'
  },
  rateCap: {
    what: 'The ceiling for each age group.',
    why: 'A forecast above the best result that age group has ever achieved, plus the biggest improvement allowed for that group, cannot be right. It is a different thing from Largest yearly change in retention, which limits how fast a rate can move from one year to the next.',
    how: 'Highest real rate in the age group × that group\'s largest rise allowed. Each group uses its own limit, never another group\'s.',
    when: 'Read only. It follows your data and your trend limits.'
  },
  qa: {
    what: 'Highlights forecast cells that look out of line with real results.',
    why: 'Problems are caught before a forecast is shared, while the numbers stay untouched.',
    how: 'A cell turns red when its repeat rate is above the warning level × the normal rate for cohorts of that age, or above the safety ceiling. Hover a red cell to see how it was calculated.',
    when: 'Nothing to change. Treat each red cell as a question to answer.'
  },
  qaMultiple: {
    what: 'How many times the normal rate for its age a forecast cell can reach before it turns red.',
    why: 'Consistent models such as subscriptions get a tight level; more variable ones get a looser level, so warnings stay meaningful.',
    how: 'Set from your own history so {qaPercentile} in 100 real results would pass.',
    when: 'Read only.'
  },
  always: {
    what: 'Two rules that are always on and have no settings.',
    why: 'Forecasts are built from real months only, so errors cannot compound year after year. The trend is measured over full years and by age group, so seasonal swings cannot pass for a trend.',
    how: 'Forecast cells more than a year ahead start from the latest real result at the same age. The trend compares the latest {trendWindow} months of cohorts with the {trendWindow} before, separately for each age group.',
    when: 'Nothing to change.'
  },
  where: {
    what: 'Every channel and cohort model, side by side.',
    why: 'It shows at a glance which values someone has changed and where the red cells are.',
    how: 'Values set by a user counts the boxes someone has typed into; hover to see which. Select a row to open that channel and cohort model.',
    when: 'Review it before publishing a version.'
  },
  admin: {
    what: 'The rules the system follows to work out every default on this screen.',
    why: 'Using the same method everywhere keeps defaults consistent, explainable and auditable.',
    how: 'A change applies to every channel and cohort model in this draft and never touches a published version.',
    when: 'Only when the method itself needs to change.'
  },
  evidenceTarget: {
    what: 'The minimum expected repeat orders behind each average.',
    why: 'With too few orders, a single customer can swing a repeat rate.',
    how: 'Used to set Minimum first month orders to learn from a cohort.',
    when: 'Only when the method itself needs to change.'
  },
  matureAgeFrom: {
    what: 'The first age used to measure the typical repeat rate.',
    why: 'Early cancellations make the first months\' repeat rates jump around.',
    how: 'Feeds the typical repeat rate, which sets Minimum first month orders.',
    when: 'Only when the method itself needs to change.'
  },
  matureAgeTo: {
    what: 'The last age used to measure the typical repeat rate.',
    why: 'At older ages fewer cohorts have data, so the rate gets thin.',
    how: 'Feeds the typical repeat rate, which sets Minimum first month orders.',
    when: 'Only when the method itself needs to change.'
  },
  matureHistoryMonths: {
    what: 'How far back cohorts count when measuring the typical repeat rate.',
    why: 'Recent cohorts reflect the business as it is today.',
    how: 'Only cohorts from the last {matureHistoryMonths} months are used.',
    when: 'Only when the method itself needs to change.'
  },
  steadyRun: {
    what: 'The number of months in a row with big enough cohorts that marks the end of the bundle.',
    why: 'A steady run shows early, messy history is over.',
    how: 'Sets the default for Bundle cohorts that started before.',
    when: 'Only when the method itself needs to change.'
  },
  tailStartAge: {
    what: 'The earliest age used to measure the monthly fade.',
    why: 'Buying settles after the first year, so earlier ages would make the fade too steep.',
    how: 'Sets which ages the Long-term fade is measured from.',
    when: 'Only when the method itself needs to change.'
  },
  qaPercentile: {
    what: 'The share of real results that must sit below the warning level.',
    why: 'Set high, red cells are rare enough to be worth a look every time.',
    how: 'Sets the Warning level for red cells.',
    when: 'Only when the method itself needs to change.'
  },
  retentionChange: {
    what: 'A planned change in how many customers come back, applied to every forecast repeat rate, the bundle included.',
    why: 'The forecast follows the data. When you expect retention to move, for example after a new loyalty scheme, this is the one place to say so.',
    how: 'Every forecast repeat rate from the start month is multiplied by 1 + the change, after typed rates carry forward and before the safety ceiling and red cell checks, so a large change is still caught. First month orders and typed rates never change. 0% by default.',
    when: 'For a bull or bear case, or when you know retention is changing. The Year on year signal below is a guide.'
  },
  retentionChangeStart: {
    what: 'The first month the retention change applies.',
    why: 'A change you plan for later should not move the months before it.',
    how: 'The first forecast month by default.',
    when: 'When the change starts later, for example the launch month of a new scheme.'
  },
  retentionSignal: {
    what: 'How the newest cohorts compare with the cohorts born a year earlier at the same ages, for each age group.',
    why: 'It shows whether retention is improving or falling, so the retention change can be set with evidence.',
    how: 'For each age group, the newest 12 cohorts at each age against the cohort born 12 months earlier at the same age, size-weighted. Pairs counts the comparisons behind it.',
    when: 'Read only. The forecast never uses it by itself.'
  },
  own: {
    what: 'Lets mature cohorts continue from their own history instead of the tab method.',
    why: 'A cohort with many months of actuals tells you more about itself than an average of other cohorts.',
    how: 'The cohort\'s latest {k} actual months, each carried to its latest age with the typical decay of recent cohorts, set the starting level. Each future month applies the typical decay for that age. Newer cohorts follow the tab method and change when it changes. A method set on a cohort always wins.',
    when: 'Read the method button next to each cohort to see which method it uses.'
  },
  ownHistoryOn: {
    what: 'Whether mature cohorts use Latest actual + typical decay from their own history.',
    why: 'It is on only when the back test shows it forecasts the held out months better than the tab method.',
    how: 'Set by the back test each time the cutoff moves: on only when its error is lower than off.',
    when: 'Turn it off to make every cohort follow the tab method.'
  },
  ownHistoryMinMonths: {
    what: 'How many months of actuals a cohort needs before it uses its own history.',
    why: 'Too few months make the starting level noisy; too many leave good history unused.',
    how: 'Set by the back test from 2 to 12 months, keeping the value with the lowest error.',
    when: 'Rarely. Raise it if young cohorts look jumpy.'
  },
  bundleRetentionWindow: {
    what: 'How many of the bundle\'s latest months measure its yearly share kept.',
    why: 'The latest months reflect old customers as they are today, while matching each month with the same month a year earlier cancels seasonality.',
    how: 'Set by the back test from 3 to 12 months. Only months where both the month and the month a year earlier are on or after the bundle cutoff count. With fewer such months, it needs more months and the last 12 months against the 12 before are used.',
    when: 'Rarely. Shorter reacts faster; longer is steadier.'
  },
  ceilingHeadroom: {
    what: 'How far above the best real rate in each age group the safety ceiling sits.',
    why: 'The ceiling should catch errors, not normal growth between cohorts.',
    how: 'The median rise between consecutive well-sized cohorts at the same age in each age group, counting only rises.',
    when: 'Raise it if good forecasts are being capped; lower it to catch more.'
  },
  autoWindow: {
    what: 'How many of the latest months of actuals Auto uses to test each forecast option.',
    why: 'Recent months show which option fits the business as it is today.',
    how: 'Auto forecasts the last {autoWindow} months from the data before them and picks the option closest to what actually happened.',
    when: 'Only when the method itself needs to change.'
  }
};

export const GUARD_HELP_SEASON_RATIO: Record<string, Partial<GuardHelp>> = {
  season: {
    what: 'How much busier or quieter each calendar month is for repeat orders than the months around it.',
    how: 'For each calendar month, how much busier or quieter it was than the months around it, measured from well-sized cohorts and averaged across the years available. Scaled so the 12 factors average 1. A month with fewer than {k} cohorts uses 1. With under 12 months of actuals every factor is 1.',
    when: 'Read only. Turn on Monthly seasonality in the forecast method to use it. Each forecast is adjusted by its month\'s factor divided by the factors of the months its source rates came from, so a busy month is never lifted twice.'
  },
  cap: {
    how: 'For each age group: the best real repeat rate any well-sized cohort has reached, with seasonality taken out, × (1 + headroom). Forecasts are checked against it before the month factor, so a busy month is judged against what is normal for that month.'
  },
  rateCap: {
    how: 'Highest real rate in the age group with seasonality taken out × (1 + headroom). With Monthly seasonality on, each forecast month\'s ceiling is this × that month\'s factor.',
    when: 'Read only. It follows your data and the headroom.'
  },
  qa: {
    how: 'A cell turns red when its repeat rate, with its month\'s seasonality taken out, is above the warning level × the normal rate for cohorts of that age, or when the safety ceiling held it down. Hover a red cell to see how it was calculated.'
  },
  legacyRetention: {
    how: 'Measured over the bundle\'s latest 12 months against the 12 months before, so a full year cancels seasonality. With less history, it uses how mature cohorts fade.'
  },
  own: {
    what: 'Older cohorts always continue from their own history; newer cohorts use the method chosen in the forecast bar.',
    how: 'Once a cohort has the threshold number of actual months, its latest {k} actual months, each carried to its latest age with the typical decay of recent cohorts, set the starting level. Each future month applies the typical decay for that age. A method set on a cohort always wins.',
    when: 'Read the method chip next to each cohort: Decay means it is an older cohort.'
  },
  ownHistoryMinMonths: {
    what: 'How many months of actuals a cohort needs before it counts as an older cohort and uses its own history.',
    how: 'Set from your data by the back test, from 2 to 12 months, keeping the value with the lowest error. It is worked out again only when new actuals arrive, and published versions keep theirs.'
  },
  autoWindow: {
    how: 'With at least 24 months of actuals, Auto tests the latest 12 months, so every calendar month is checked. With fewer, it forecasts the last {autoWindow} months from the data before them and picks the option closest to what actually happened.'
  }
};

export function guardHelp(key: string, constants: Record<MethodConstantKey, number>, seasonRatio = false): GuardHelp | null {
  const base = GUARD_HELP_TEXT[key];
  if (!base) return null;
  const entry: GuardHelp = seasonRatio && GUARD_HELP_SEASON_RATIO[key] ? Object.assign({}, base, GUARD_HELP_SEASON_RATIO[key]) : base;
  const values: Record<string, number> = Object.assign({}, GUARD_STRUCTURE, constants) as unknown as Record<string, number>;
  const fill = (s: string) => s.replace(/\{(\w+)\}/g, (m, k) => (typeof values[k] === 'number' ? String(values[k]) : m));
  return { what: fill(entry.what), why: fill(entry.why), how: fill(entry.how), when: fill(entry.when) };
}

export function guardHelpText(key: string, constants: Record<MethodConstantKey, number>, seasonRatio = false): string {
  const h = guardHelp(key, constants, seasonRatio);
  return h ? [h.what, h.why, h.how, h.when].join(' ') : '';
}
